import type { PlannerFirstWrite } from './planner-first-writes.js';
import type { PlannerFirstBatch } from './planner-first-batch.contract.js';

export const CONTACT_SOURCE_LABELS = {
  ru: ['источник', 'из', 'пришёл из', 'пришла из', 'по рекомендации', 'через'],
  en: ['source', 'from', 'via', 'referred by'],
} as const;

/** A source value is accepted only when its own operation explicitly labels it. */
export function contactSourceHasLabel(write: PlannerFirstWrite): boolean {
  if (write.capability !== 'create_contact' || !write.source) return true;
  const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
  const labels = Object.values(CONTACT_SOURCE_LABELS).flat().map(escape).join('|');
  return new RegExp(
    `(?<![\\p{L}\\p{N}_])(?:${labels})[\\s:—–-]+${escape(write.source.rawValue)}(?![\\p{L}\\p{N}_])`,
    'iu',
  ).test(write.sourceQuote);
}

// Existing read remainder markers; the expression is kept byte-for-byte equivalent.
export const READ_UNCOVERED_MARKERS =
  /(?:\b(?:создай|поставь|удали|измени|перенеси|подготовь|отправь|create|delete|update|send|draft)\b|создай|поставь|удали|измени|перенеси|подготовь|отправь|кроме|совет|проанализируй|анализ|до обеда|больше|меньше|дороже|дешевле|only mine|только мои|без суммы|без ответственного|без следующего шага|без активности)/iu;

const MARKERS = [
  /(?<![\p{L}\p{N}_])(?:и|and)\s+(?:отправь|напиши|позвони|удали|создай|перенеси|измени|send|write|call|delete|create|move|reschedule|update)(?![\p{L}\p{N}_])/giu,
  /(?<![\p{L}\p{N}_])(?:потом|затем|а также|then|also)(?![\p{L}\p{N}_])/giu,
  /(?<![\p{L}\p{N}_])(?:если|когда|при условии|if|unless|only if)(?![\p{L}\p{N}_])/giu,
  /(?<![\p{L}\p{N}_])(?:кроме|не трогай|не меняй|не звонить|не звони|except|don't|do not)(?![\p{L}\p{N}_])/giu,
  /(?<![\p{L}\p{N}_])(?:тег(?:ом|и|а|у|ов)?|tags?|вложени[еяй]|файл(?:ы|ов|а)?|attachments?|files?|приоритет(?:ом|а)?|priority|исполнитель(?:я|ю)?|ответственн(?:ый|ого|ому|ым)|assignee|owner)(?![\p{L}\p{N}_])|#[\p{L}\p{N}_]+/giu,
  /(?<![\p{L}\p{N}_])(?:отправь|напиши\s+письмо|в\s+телеграм|send|email)(?![\p{L}\p{N}_])/giu,
];

const WRITE_VERBS =
  /(?<![\p{L}\p{N}_])(?:создай|создать|добавь|добавить|поставь|подготовь|отметь|заверши|перенеси|переведи|измени|удали|напиши|позвони|отправь|запиши|create|add|prepare|mark|complete|move|reschedule|update|delete|write|call|send)(?![\p{L}\p{N}_])/giu;
const PRIMARY: Readonly<Record<PlannerFirstWrite['capability'] | 'batch', readonly string[]>> = {
  create_task: [
    'создай',
    'создать',
    'добавь',
    'добавить',
    'поставь',
    'подготовь',
    'create',
    'add',
    'prepare',
  ],
  complete_task: ['отметь', 'заверши', 'mark', 'complete'],
  reschedule_task: ['перенеси', 'измени', 'move', 'reschedule', 'update'],
  create_contact: ['создай', 'создать', 'добавь', 'добавить', 'create', 'add'],
  add_note: ['добавь', 'добавить', 'запиши', 'напиши', 'add', 'write'],
  change_deal_stage: ['перенеси', 'переведи', 'измени', 'move', 'update'],
  batch: ['создай', 'создать', 'добавь', 'поставь', 'подготовь', 'create', 'add', 'prepare'],
};

type Fact = { rawValue: string; sourceQuote: string };
function facts(value: unknown): Fact[] {
  if (!value || typeof value !== 'object') return [];
  if (Array.isArray(value)) return value.flatMap(facts);
  if (
    'rawValue' in value &&
    'sourceQuote' in value &&
    typeof value.rawValue === 'string' &&
    typeof value.sourceQuote === 'string'
  )
    return [{ rawValue: value.rawValue, sourceQuote: value.sourceQuote }];
  return Object.entries(value)
    .filter(([key]) => key !== 'sourceQuote')
    .flatMap(([, item]) => facts(item));
}

function coveredSpans(source: string, value: PlannerFirstWrite | PlannerFirstBatch): Uint8Array {
  const covered = new Uint8Array(source.length);
  const protect = (fragment: string) => {
    const start = source.indexOf(fragment);
    if (fragment && start >= 0 && source.indexOf(fragment, start + 1) < 0)
      covered.fill(1, start, start + fragment.length);
  };
  for (const fact of facts(value)) {
    if (source.includes(fact.sourceQuote) && fact.sourceQuote.includes(fact.rawValue))
      protect(fact.rawValue);
  }
  if ('relationQuote' in value) protect(value.relationQuote);
  if ('capability' in value && value.capability === 'create_contact' && value.email) {
    const label = new RegExp(
      `(?<![\\p{L}\\p{N}_])email(?=\\s+${value.email.rawValue.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&')})`,
      'iu',
    ).exec(source);
    if (label) covered.fill(1, label.index, label.index + label[0].length);
  }
  return covered;
}

/** Mark only unique literal values as covered; a broad operation quote is not a field. */
export function plannerWriteCoverageMarker(
  source: string,
  value: PlannerFirstWrite | PlannerFirstBatch,
): string | undefined {
  const covered = coveredSpans(source, value);
  for (const pattern of MARKERS) {
    pattern.lastIndex = 0;
    for (const match of source.matchAll(pattern)) {
      const start = match.index;
      if (!covered.slice(start, start + match[0].length).every(Boolean)) return match[0];
    }
  }
  const allowed = PRIMARY['capability' in value ? value.capability : 'batch'];
  let primarySeen = false;
  WRITE_VERBS.lastIndex = 0;
  for (const match of source.matchAll(WRITE_VERBS)) {
    const start = match.index;
    if (covered.slice(start, start + match[0].length).every(Boolean)) continue;
    if (allowed.includes(match[0].toLocaleLowerCase()) && !primarySeen) primarySeen = true;
    else return match[0];
  }
  return undefined;
}

/** Explicit service vocabulary; facts are never treated as generic language. */
export const PLANNER_WRITE_SERVICE_WORDS = {
  ru: [
    'создай',
    'создать',
    'добавь',
    'добавить',
    'поставь',
    'подготовь',
    'отметь',
    'заверши',
    'перенеси',
    'переведи',
    'измени',
    'запиши',
    'пометить',
    'нужно',
    'нужна',
    'мне',
    'задача',
    'задачу',
    'задачи',
    'заметку',
    'заметка',
    'сделку',
    'сделки',
    'сделке',
    'контакт',
    'контакта',
    'контакту',
    'телефоном',
    'телефон',
    'почтой',
    'email',
    'компании',
    'каждому',
    'каждого',
    'каждой',
    'для',
    'на',
    'по',
    'как',
    'ему',
    'ей',
    'им',
    'пожалуйста',
    'новая',
    'новый',
    'срок',
    'названием',
    'без',
    'активности',
    'дней',
  ],
  en: [
    'create',
    'add',
    'prepare',
    'mark',
    'complete',
    'move',
    'reschedule',
    'rescheduled',
    'update',
    'task',
    'tasks',
    'note',
    'deal',
    'deals',
    'contact',
    'contacts',
    'company',
    'each',
    'every',
    'all',
    'for',
    'to',
    'on',
    'in',
    'with',
    'of',
    'the',
    'and',
    'as',
    'him',
    'her',
    'me',
    'please',
    'need',
    'should',
    'be',
    'entitled',
    'marked',
    'inactive',
    'days',
    'phone',
  ],
} as const;

const SERVICE_WORDS = new Set<string>(Object.values(PLANNER_WRITE_SERVICE_WORDS).flat());
export const PLANNER_FIRST_REMAINDER_ENFORCED: boolean = false;

const CONTEXT_POINTERS =
  /(?<![\p{L}\p{N}_])(?:here|здесь|тут|сюда|к\s+этой\s+сделке|к\s+этому\s+контакту|this\s+deal|this\s+contact)(?![\p{L}\p{N}_])/giu;

/** Return the first uncovered significant token in source order, preserving original bytes. */
export function plannerWriteCoverageRemainder(
  source: string,
  value: PlannerFirstWrite | PlannerFirstBatch,
): string | undefined {
  const covered = coveredSpans(source, value);
  if (
    'target' in value &&
    value.target &&
    'source' in value.target &&
    value.target.source === 'trusted_context'
  ) {
    CONTEXT_POINTERS.lastIndex = 0;
    for (const match of source.matchAll(CONTEXT_POINTERS))
      covered.fill(1, match.index, match.index + match[0].length);
  }
  const remaining = source
    .split('')
    .map((character, index) => (covered[index] ? ' ' : character))
    .join('');
  for (const match of remaining.matchAll(/[\p{L}\p{N}][\p{L}\p{M}\p{N}]*/gu)) {
    const token = match[0];
    if ([...token].length >= 2 && !SERVICE_WORDS.has(token.toLocaleLowerCase())) return token;
  }
  return undefined;
}
